import { CurrencyRupeeIcon } from "@heroicons/react/24/outline";
import { Button, Link } from "@heroui/react";
import { Table, TableBody, TableCell, TableColumn, TableHeader, TableRow } from "@nextui-org/table";
import { UseMutateFunction, useMutation, useQuery } from "@tanstack/react-query";
import { CreateCommanWay, GetCommanWay, ListCommanWay, PostFormData } from "API/commonApi";
import axios, { AxiosResponse } from "axios";
import { Img, Input } from "components";
import { AwbLabel } from "components/PrintLabels/AwbLabel";
import { request } from "config/request";
import moment from "moment";
import React, { useEffect, useRef, useState } from "react";
import QRCode from "react-qr-code";
import { useNavigate, useSearchParams } from "react-router-dom";
import Swal from "sweetalert2";
import UseToast from "utils/AfToast";
import { roles } from "utils/constant";
import { get_file_url } from "utils/lib-utils";
import { saveAs } from "file-saver";
import { buildEwbPartBPayload, validateEwbPartB } from "utils/ewaybill";

declare global {
  interface Window {
    SEND_WAPP: (num: any, link: any, msg: any) => void;
  }
}

const TrackingAWB = () => {
  const BASE_URL = process.env.REACT_APP_BACKEND_URL;
  const navigator = useNavigate();

  const [awbNo, setAwbNo] = useState("");
  const [isLabelOpen, setIsLabelOpen] = useState(false);
  const [awbTrackingData, setAwbTrackingData] = useState<any>({
    tracking_data: [],
    bookingDate: "",
    awbData: {},
    bookingData: {},
  });
  const [searchParams, setSearchParams] = useSearchParams();
  const [deliveryDoneInfo, setDeliveryDoneInfo] = useState<any>({});
  const [productList, setProductList] = useState<any>([]);
  const [commentsList, setCommentsList] = useState<any>([]);

  const [showImg, setShowImg] = useState<any>(false);
  const [showVolume, setShowVolume] = useState<any>(false);
  const [showPayPoup, setShowPayPoup] = useState<boolean>(false);
  const [paymentLink, setPaymentLink] = useState<string>("");
  const [qrAmount, setQrAmount] = useState<any>("");

  const [payAmount, setPayAmount] = useState<any>("");
  const [payRemark, setPayRemark] = useState<any>("");

  // Data Logger state
  const [showDataLogger, setShowDataLogger] = useState(false);
  const [dlDeviceCompany, setDlDeviceCompany] = useState("");
  const [dlDeviceNo, setDlDeviceNo] = useState("");
  const [dlFile, setDlFile] = useState<File | null>(null);
  const dlFileRef = useRef<HTMLInputElement>(null);

  const [canEditBook, setCanEditBook] = useState<any>(false);
  const [userRole, setUserRole] = useState<string>("");

  const [wappNo, setWappNo] = useState<any>("");
  const [showChangeLog, setShowChangeLog] = useState(false);
  const [changeLogEntries, setChangeLogEntries] = useState<any[]>([]);

  // Slip & Proof upload state
  const [proofPopup, setProofPopup] = useState<any>(null);
  const slipInputRef = useRef<HTMLInputElement>(null);
  const proofUploadInputRef = useRef<HTMLInputElement>(null);

  // E-Way Bill state
  const [showEwb, setShowEwb] = useState(false);
  const [ewbNo, setEwbNo] = useState<string>("");
  const [ewbData, setEwbData] = useState<any>(null);
  const [ewbHistory, setEwbHistory] = useState<any[]>([]);
  const [ewbTab, setEwbTab] = useState<"details" | "partb" | "transporter" | "history">("details");
  // Part-B form
  const [ewbVehicleNo, setEwbVehicleNo] = useState("");
  const [ewbFromPlace, setEwbFromPlace] = useState("");
  const [ewbFromState, setEwbFromState] = useState("");
  const [ewbReasonCode, setEwbReasonCode] = useState("1");
  const [ewbReasonRem, setEwbReasonRem] = useState("");
  const [ewbTransDocNo, setEwbTransDocNo] = useState("");
  const [ewbTransDocDate, setEwbTransDocDate] = useState("");
  const [ewbTransMode, setEwbTransMode] = useState("1");
  const [ewbVehicleType, setEwbVehicleType] = useState("R");
  // Transporter form
  const [ewbTransporterId, setEwbTransporterId] = useState("");
  const COMPANY_GSTIN = "29AAACQ3770E000";

  const { mutate: mutateUploadImg, isLoading: isUploading } = useMutation(
    ({ id, type, file }: { id: string; type: string; file: File }) => {
      const formData = new FormData();
      formData.append("image", file);
      formData.append("type", type);
      return PostFormData(`/admin/awbdata/imgs/${id}`, formData);
    },
    {
      onSuccess: (data: any) => {
        if (data.status === "SUCCESS") {
          UseToast("Uploaded successfully", "success");
          mutate(awbNo);
          if (proofPopup && data.data) {
            setProofPopup((prev: any) => ({ ...prev, pickImgs: data.data.pickImgs, signedSlip: data.data.signedSlip }));
          }
        } else {
          UseToast(data.message || "Upload failed", "error");
        }
      },
      onError: (err: any) => {
        UseToast(typeof err === "string" ? err : err.message, "error");
      },
    },
  );

  const { mutate: mutateDeleteImg, isLoading: isDeleting } = useMutation(
    ({ id, type, url }: { id: string; type: string; url: string }) =>
      CreateCommanWay({ url: `/admin/awbdata/imgs/delete/${id}`, data: { type, url } }),
    {
      onSuccess: (data: any) => {
        if (data.status === "SUCCESS") {
          UseToast("Deleted successfully", "success");
          mutate(awbNo);
          if (proofPopup && data.data) {
            setProofPopup((prev: any) => ({ ...prev, pickImgs: data.data.pickImgs, signedSlip: data.data.signedSlip }));
          }
        } else {
          UseToast(data.message || "Delete failed", "error");
        }
      },
      onError: (err: any) => {
        UseToast(typeof err === "string" ? err : err.message, "error");
      },
    },
  );

  const handleSlipUpload = (file: File) => {
    if (awbTrackingData?.awbData?.id) {
      mutateUploadImg({ id: awbTrackingData.awbData.id, type: "slip", file });
    }
  };

  const handleProofUpload = (file: File) => {
    if (awbTrackingData?.awbData?.id) {
      mutateUploadImg({ id: awbTrackingData.awbData.id, type: "proof", file });
    }
  };

  const handleDeleteImage = (type: string, url: string) => {
    if (window.confirm("Delete this image?") && awbTrackingData?.awbData?.id) {
      mutateDeleteImg({ id: awbTrackingData.awbData.id, type, url });
    }
  };

  const getProofUrls = (pickImgs: string) => {
    if (!pickImgs) return [];
    return pickImgs
      .split(",")
      .map((u: string) => u.trim())
      .filter(Boolean);
  };
  const SEND_WAPP = (num: any, link: any, msg: any) => {
    let pdfPath = BASE_URL;
    if (awbTrackingData?.bookingData?.customer_code) {
      pdfPath += "/invoices_c";
    } else {
      pdfPath += "/invoices";
    }
    const invoiceFileName = awbTrackingData?.awbData?._invoice_no_c_b?.is_proforma 
      ? `proforma_${awbTrackingData?.awbData?._invoice_no_c_b?.id}` 
      : (awbTrackingData?.awbData?._invoice_no_c_b?.invoice_no || "");
    pdfPath += `/${awbTrackingData?.awbData?._invoice_no_c_b?.subbranch}/${invoiceFileName}.pdf`;
    axios.request({
      url: BASE_URL + `/admin/send-wapp`,
      method: "POST",
      data: {
        mobile: num,
        pdf: pdfPath,
        msg: msg,
      },
    });
    setWappNo("");
    alert("Whatsapp Sent Successfully!");
  };

  React.useEffect(() => {
    const userD = (() => { const auth = localStorage.getItem("auth"); return auth ? JSON.parse(auth) : {}; })();
    if (Object.keys(userD).length) {
      setUserRole(userD.state.user.roleName);
      if (
        userD.state.user.role_name == 1 ||
        userD.state.user.role_name == 2 ||
        userD.state.user.role_name == 3 ||
        userD.state.user.roleName == "CUSTOMER SERVICE"
      ) {
        setCanEditBook(true);
      }
      if (userD.state.user.roleName == "CUSTOMER") {
        navigator("/");
      }
    }
  }, []);

  const { isFetching, isLoading, refetch } = useQuery(["admin_product_list"], () => ListCommanWay("/admin/product/list", {}, {}), {
    keepPreviousData: true,
    onSuccess(data) {
      if (data.status == "SUCCESS") {
        setProductList(data.data.data);
      }
    },
    onError: (data) => {},
  });

  const { refetch: commnetsRefetching } = useQuery(
    ["comments_list"],
    () =>
      ListCommanWay(
        "/admin/awbcomment/list",
        {
          awb_id: awbTrackingData?.awbData?.id,
        },
        { include: ["_addedBy"] },
      ),
    {
      keepPreviousData: true,
      enabled: false,
      onSuccess(data) {
        if (data.status == "SUCCESS") {
          setCommentsList(data.data.data);
        }
      },
      onError: (data) => {},
    },
  );

  useEffect(() => {
    if (awbTrackingData?.awbData?.id) {
      commnetsRefetching();
    }
  }, [awbTrackingData]);

  const {
    mutate,
    isLoading: isloading,
  }: {
    mutate: UseMutateFunction<AxiosResponse<any, any>, unknown, any, unknown>;
    isLoading: any;
  } = useMutation((awb_no) => GetCommanWay("/admin/proof_of_delivery/track/" + awb_no, {}, {}), {
    onSuccess: (data: any) => {
      console.log(data, data.status == "SUCCESS");
      if (data.status == "SUCCESS") {
        setAwbTrackingData(data.data);
        setDeliveryDoneInfo({});
        if (data.data.tracking_data) {
          try {
            data.data.tracking_data.forEach((t) => {
              if (t.message_code == "Shipment Delivered" || t.message_code == "0 - Shipment Delivered") {
                let recv_mobile = "";
                if (t.remark.includes(" | ")) {
                  recv_mobile = t.remark?.split(" | ")?.[0];
                } else {
                  if (t.remark.length == 10 && /^\d+$/.test(t.remark)) {
                    recv_mobile = t.remark;
                  }
                }
                setDeliveryDoneInfo({ ...t, recv_mobile });
              }
            });
          } catch (e) {}
        }
      } else {
        UseToast(data.message, "error");
      }
    },
    onError: (data: any) => {
      typeof data === "string" ? UseToast(data, "error") : UseToast(data.message, "error");
    },
  });
  const handleSubmit = () => {
    setAwbTrackingData({
      tracking_data: [],
      bookingDate: "",
      awbData: {},
      bookingData: {},
    });
    mutate(awbNo);
  };

  useEffect(() => {
    const awbParam = searchParams.get("awb");
    if (awbParam) {
      setAwbNo(awbParam);
      setAwbTrackingData({
        tracking_data: [],
        bookingDate: "",
        awbData: {},
        bookingData: {},
      });
      mutate(awbParam);
    }
  }, [searchParams]);

  const getTRline = (name, value, isEven: any = false) => {
    return (
      <tr className={`text-sm ${isEven ? "bg-slate-50" : ""}`}>
        <td className="text-right pr-3 py-1.5 whitespace-nowrap text-slate-500 font-medium">{name}</td>
        <td className="py-1.5 text-black-900">{value}</td>
      </tr>
    );
  };

  const getTRline2 = (name, value, isEven: any = false) => {
    return (
      <TableRow>
        <TableCell className="text-right pr-4 whitespace-nowrap text-slate-500 text-sm font-medium">{name}</TableCell>
        <TableCell className="text-slate-700 text-sm">{value}</TableCell>
      </TableRow>
    );
  };

  const getChargeableWeight = (weight, product_code, ll, bb, hh) => {
    ll = Number(ll);
    bb = Number(bb);
    hh = Number(hh);
    let dWeight = (ll * bb * hh) / (product_code == "X" ? 2700 : 5000);
    return Math.max(Number(weight), dWeight);
  };

  const {
    mutate: mutateUpiQr,
    isLoading: isQrLoading,
  }: {
    mutate: UseMutateFunction<AxiosResponse<any, any>, unknown, any, unknown>;
    isLoading: any;
  } = useMutation((data) => CreateCommanWay(data), {
    onSuccess: (data: any) => {
      console.log(data, data.status == "SUCCESS");
      if (data.status == "SUCCESS") {
        setPaymentLink(data.data.paymentLink);
        setQrAmount(data.data.amount);
      } else {
        setPaymentLink("");
        UseToast(data.message, "error");
      }
    },
    onError: (data: any) => {
      setPaymentLink("");
      typeof data === "string" ? UseToast(data, "error") : UseToast(data.message, "error");
    },
  });

  const {
    mutate: mutatePayStatus,
  }: {
    mutate: UseMutateFunction<AxiosResponse<any, any>, unknown, any, unknown>;
    isLoading: any;
  } = useMutation((data) => CreateCommanWay(data), {
    onSuccess: (data: any) => {
      if (data.status == "SUCCESS" && data.data?.isFullPaid) {
        window.location.reload();
      }
    },
    onError: (data: any) => {
      typeof data === "string" ? UseToast(data, "error") : UseToast(data.message, "error");
    },
  });

  const {
    mutate: mutateCheckPayStatus,
    isLoading: isCheckPayLoading,
  }: {
    mutate: UseMutateFunction<AxiosResponse<any, any>, unknown, any, unknown>;
    isLoading: any;
  } = useMutation((data) => CreateCommanWay(data), {
    onSuccess: (data: any) => {
      if (data.status == "SUCCESS" && data.data?.isFullPaid) {
        UseToast("Payment confirmed! UTR: " + (data.data.utrNo || ""), "success");
        setTimeout(() => window.location.reload(), 1500);
      } else {
        UseToast(data.data?.payStatus || "Payment not confirmed yet", "error");
      }
    },
    onError: (data: any) => {
      typeof data === "string" ? UseToast(data, "error") : UseToast(data.message, "error");
    },
  });

  const {
    mutate: mutateComment,
    isLoading: isCommentloading,
  }: {
    mutate: UseMutateFunction<AxiosResponse<any, any>, unknown, any, unknown>;
    isLoading: any;
  } = useMutation((data) => CreateCommanWay(data), {
    onSuccess: (data: any) => {
      console.log(data, data.status == "SUCCESS");
      if (data.status == "SUCCESS") {
        window.location.reload();
      } else {
        UseToast(data.message, "error");
      }
    },
    onError: (data: any) => {
      typeof data === "string" ? UseToast(data, "error") : UseToast(data.message, "error");
    },
  });

  const { mutate: mutateChangeLog, isLoading: isChangeLogLoading } = useMutation(
    async ({ awbId, pickupId }: { awbId: string; pickupId?: string }) => {
      const bookingResp = await GetCommanWay(`/admin/booking/change-log/${awbId}`, {}, {});

      let pickupResp: any = null;
      if (pickupId) {
        pickupResp = await GetCommanWay(`/admin/pickup/change-log/${pickupId}`, {}, {});
      }

      return { bookingResp, pickupResp };
    },
    {
      onSuccess: (resp: any) => {
        const bookingResp = resp?.bookingResp;
        const pickupResp = resp?.pickupResp;

        if (bookingResp?.status === "SUCCESS" && (!pickupResp || pickupResp?.status === "SUCCESS")) {
          const bookingList = ((bookingResp?.data?.data || bookingResp?.data || []) as any[]).map((entry: any) => ({
            ...entry,
            _logSource: "BOOKING",
          }));
          const pickupList = ((pickupResp?.data?.data || pickupResp?.data || []) as any[]).map((entry: any) => ({
            ...entry,
            _logSource: "PICKUP",
          }));

          const mergedList = [...bookingList, ...pickupList];
          const sortedList = [...mergedList].sort((a, b) => {
            const at = new Date(a?.createdAt || a?.updatedAt || 0).getTime();
            const bt = new Date(b?.createdAt || b?.updatedAt || 0).getTime();
            return at - bt;
          });

          setChangeLogEntries(sortedList);
          setShowChangeLog(true);
        } else {
          const msg = bookingResp?.message || pickupResp?.message || "Failed to load changelog";
          UseToast(msg, "error");
        }
      },
      onError: (err: any) => {
        UseToast(typeof err === "string" ? err : err.message, "error");
      },
    },
  );

  // ── E-Way Bill mutations ──
  const { mutate: mutateEwbGet, isLoading: isEwbLoading } = useMutation(
    (data: any) => CreateCommanWay({ url: "/admin/ewaybill/get", data }),
    {
      onSuccess: (data: any) => {
        if (data.status === "SUCCESS") {
          setEwbData(data.data.ewaybill);
          setEwbHistory(data.data.partBHistory || []);
        } else {
          UseToast(data.message || "Failed to fetch E-Way Bill", "error");
        }
      },
      onError: (err: any) => UseToast(typeof err === "string" ? err : err.message, "error"),
    },
  );

  const { mutate: mutateEwbPartB, isLoading: isEwbPartBLoading } = useMutation(
    (data: any) => CreateCommanWay({ url: "/admin/ewaybill/update-partb", data }),
    {
      onSuccess: (data: any) => {
        if (data.status === "SUCCESS") {
          UseToast("Part-B (Vehicle) updated successfully", "success");
          // Refresh EWB data
          mutateEwbGet({ gstin: COMPANY_GSTIN, ewbNo: Number(ewbNo), forceRefresh: true });
          mutateEwbHistory({ ewbNo: Number(ewbNo) });
        } else {
          UseToast(data.message || "Part-B update failed", "error");
        }
      },
      onError: (err: any) => UseToast(typeof err === "string" ? err : err.message, "error"),
    },
  );

  const { mutate: mutateEwbTransporter, isLoading: isEwbTransporterLoading } = useMutation(
    (data: any) => CreateCommanWay({ url: "/admin/ewaybill/update-transporter", data }),
    {
      onSuccess: (data: any) => {
        if (data.status === "SUCCESS") {
          UseToast("Transporter updated successfully", "success");
          mutateEwbGet({ gstin: COMPANY_GSTIN, ewbNo: Number(ewbNo), forceRefresh: true });
          mutateEwbHistory({ ewbNo: Number(ewbNo) });
        } else {
          UseToast(data.message || "Transporter update failed", "error");
        }
      },
      onError: (err: any) => UseToast(typeof err === "string" ? err : err.message, "error"),
    },
  );

  const { mutate: mutateEwbPdf, isLoading: isEwbPdfLoading } = useMutation(
    (data: any) =>
      request({ url: "/admin/ewaybill/pdf", method: "POST", body: data, responseType: "blob" }),
    {
      onSuccess: (data: any) => {
        const blob = new Blob([data.data], { type: "application/pdf" });
        saveAs(blob, `EWB_${ewbNo}.pdf`);
        UseToast("PDF downloaded", "success");
      },
      onError: (err: any) => UseToast(typeof err === "string" ? err : err.message, "error"),
    },
  );

  const { mutate: mutateEwbHistory, isLoading: isEwbHistoryLoading } = useMutation(
    (data: any) => CreateCommanWay({ url: "/admin/ewaybill/history", data }),
    {
      onSuccess: (data: any) => {
        if (data.status === "SUCCESS") {
          setEwbHistory(data.data || []);
        }
      },
      onError: (err: any) => UseToast(typeof err === "string" ? err : err.message, "error"),
    },
  );

  const openEwbPopup = () => {
    const ebNo = String(awbTrackingData?.bookingData?.ewayBillNo || "");
    setEwbData(null);
    setEwbHistory([]);
    setEwbTab("details");
    setEwbVehicleNo("");
    setEwbFromPlace("");
    setEwbFromState("");
    setEwbReasonCode("1");
    setEwbReasonRem("");
    setEwbTransDocNo(awbTrackingData?.awbData?.awb_no || "");
    setEwbTransDocDate(moment().format("DD/MM/YYYY"));
    setEwbTransMode("1");
    setEwbVehicleType("R");
    setEwbTransporterId("");
    setEwbNo(ebNo);
    setShowEwb(true);
    // Auto-fetch on open
    if (ebNo) {
      mutateEwbGet({ gstin: COMPANY_GSTIN, ewbNo: Number(ebNo) });
      mutateEwbHistory({ ewbNo: Number(ebNo) });
    }
  };

  const handleEwbFetch = () => {
    if (!ewbNo) return UseToast("Enter E-Way Bill number", "error");
    mutateEwbGet({ gstin: COMPANY_GSTIN, ewbNo: Number(ewbNo) });
    mutateEwbHistory({ ewbNo: Number(ewbNo) });
  };

  const handleEwbPartBSubmit = () => {
    const validationError = validateEwbPartB({
      ewbNo,
      transMode: ewbTransMode,
      vehicleNo: ewbVehicleNo,
      transDocNo: ewbTransDocNo,
    });

    if (validationError) return UseToast(validationError, "error");

    mutateEwbPartB(
      buildEwbPartBPayload({
        gstin: COMPANY_GSTIN,
        ewbNo: Number(ewbNo),
        vehicleNo: ewbVehicleNo,
        fromPlace: ewbFromPlace,
        fromState: ewbFromState,
        reasonCode: ewbReasonCode,
        reasonRem: ewbReasonRem,
        transDocNo: ewbTransDocNo,
        transDocDate: ewbTransDocDate,
        transMode: ewbTransMode,
        vehicleType: ewbVehicleType,
      }),
    );
  };

  const handleEwbTransporterSubmit = () => {
    if (!ewbNo || !ewbTransporterId) return UseToast("Transporter GSTIN is required", "error");
    mutateEwbTransporter({
      gstin: COMPANY_GSTIN,
      ewbNo: Number(ewbNo),
      transporterId: ewbTransporterId,
    });
  };

  const handleEwbPdfDownload = () => {
    if (!ewbNo) return UseToast("Enter E-Way Bill number", "error");
    mutateEwbPdf({ gstin: COMPANY_GSTIN, ewbNo: Number(ewbNo) });
  };

  const handleClickComment = () => {
    Swal.fire({
      title: "Post Comment",
      text: "",
      input: "text",
      confirmButtonText: "Save",
      showCancelButton: true,
      inputPlaceholder: "Enter Comment",
      inputAttributes: {
        style: "border: 1px solid gray; border-radius: 8px",
      },
    }).then((x) => {
      if (x.isConfirmed) {
        console.log(x);
        mutateComment({
          url: "/admin/awbcomment/create",
          data: { comment: x.value, awb_id: awbTrackingData?.awbData?.id },
        });
      }
    });
  };

  const handlePayButton = () => {
    setShowPayPoup(true);
    mutateUpiQr({
      url: "/admin/upi/qr-gen",
      data: { invoice: awbTrackingData?.awbData?.invoice_no_c_b },
    });
  };

  const checkPayStatus = () => {
    mutateCheckPayStatus({
      url: "/admin/upi/qr-pay-status",
      data: { traNo: awbTrackingData?.awbData?._invoice_no_c_b?.utrNo },
    });
  };

  const handleManualPayButton = () => {
    setShowPayPoup(true);
    mutatePayStatus({
      url: "/admin/upi/manual-pay",
      data: { invoice: awbTrackingData?.awbData?.invoice_no_c_b, remark: payRemark },
    });
  };

  // Data Logger mutation
  const { mutate: mutateDataLogger, isLoading: isDlSubmitting } = useMutation(
    ({ id, deviceCompany, deviceNo, file }: { id: string; deviceCompany: string; deviceNo: string; file: File | null }) => {
      const formData = new FormData();
      formData.append("deviceCompany", deviceCompany);
      formData.append("deviceNo", deviceNo);
      if (file) formData.append("file", file);
      return PostFormData(`/admin/awbdata/data-logger/${id}`, formData);
    },
    {
      onSuccess: (data: any) => {
        if (data.status === "SUCCESS") {
          UseToast("Data Logger updated successfully", "success");
          setShowDataLogger(false);
          setDlDeviceCompany("");
          setDlDeviceNo("");
          setDlFile(null);
          // Refresh tracking data
          mutate(awbNo);
        } else {
          UseToast(data.message || "Update failed", "error");
        }
      },
      onError: (err: any) => {
        UseToast(typeof err === "string" ? err : err.message, "error");
      },
    },
  );

  const getDataLoggerLog = () => {
    try {
      const raw = awbTrackingData?.awbData?.dataLoggerLog;
      if (!raw) return {};
      return typeof raw === "string" ? JSON.parse(raw) : raw;
    } catch {
      return {};
    }
  };

  const openDataLoggerPopup = () => {
    const dl = getDataLoggerLog();
    setDlDeviceCompany(dl.deviceCompany || "");
    setDlDeviceNo(dl.deviceNo || "");
    setDlFile(null);
    if (dlFileRef.current) dlFileRef.current.value = "";
    setShowDataLogger(true);
  };

  const formatChangeValue = (value: any) => {
    if (value === null || value === undefined || value === "") return "-";
    if (typeof value === "object") return JSON.stringify(value);

    const val = String(value).trim();
    const m = moment(val, ["DD-MM-YYYY HH:mm:ss", "DD-MM-YYYY HH:mm", "YYYY-MM-DD", moment.ISO_8601], true);
    if (m.isValid()) {
      return m.format("DD-MM-YYYY HH:mm:ss");
    }
    return val;
  };

  const getFormattedChanges = (changeJson: any) => {
    if (!changeJson) return [];

    let parsed = changeJson;
    if (typeof changeJson === "string") {
      try {
        parsed = JSON.parse(changeJson);
      } catch {
        return [];
      }
    }

    const fieldLabelMap: Record<string, string> = {
      pkg1Rate: "1 Ltr",
      pkg2Rate: "4 Ltr",
      pkg3Rate: "10 Ltr",
    };

    return Object.entries(parsed || {})
      .map(([field, value]: any) => {
        const before = Array.isArray(value) ? value[0] : "-";
        const after = Array.isArray(value) ? value[1] : value;
        return {
          field: fieldLabelMap[field] || field,
          before: formatChangeValue(before),
          after: formatChangeValue(after),
        };
      })
      .filter((x: any) => x.before !== x.after);
  };

  const { mutate: mutateRegenInvoice, isLoading: isRegenLoading } = useMutation(
    (invoiceId: string) => GetCommanWay(`/admin/booking/regenInvoice/${invoiceId}`, {}, {}),
    {
      onSuccess: (data: any) => {
        if (data.status === "SUCCESS") {
          UseToast("Invoice regenerated successfully", "success");
          mutate(awbNo);
        } else {
          UseToast(data.message || "Regen failed", "error");
        }
      },
      onError: (err: any) => {
        UseToast(typeof err === "string" ? err : err.message, "error");
      },
    },
  );

  const handleRegenInvoice = () => {
    const invoiceId = awbTrackingData?.awbData?._invoice_no_c_b?.id;
    if (!invoiceId) return;
    Swal.fire({
      title: "Regenerate Invoice?",
      text: "This will regenerate the cash invoice PDF.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, Regen",
    }).then((result) => {
      if (result.isConfirmed) {
        mutateRegenInvoice(invoiceId);
      }
    });
  };

  const { mutate: mutateCancelIRN, isLoading: isCancelIRNLoading } = useMutation(
    (data: any) => CreateCommanWay(data),
    {
      onSuccess: (data: any) => {
        if (data.status === "SUCCESS") {
          UseToast("IRN cancelled successfully", "success");
          mutate(awbNo);
        } else {
          UseToast(data.message || "Cancel failed", "error");
        }
      },
      onError: (err: any) => {
        UseToast(typeof err === "string" ? err : err.message, "error");
      },
    },
  );

  const handleCancelIRN = () => {
    const invoiceId = awbTrackingData?.awbData?._invoice_no_c_b?.id;
    if (!invoiceId) return;
    Swal.fire({
      title: "Cancel IRN?",
      text: "Select a reason for IRN cancellation.",
      icon: "warning",
      input: "select",
      inputOptions: {
        "1": "Duplicate",
        "2": "Data Entry Mistake",
      },
      inputPlaceholder: "Select reason",
      showCancelButton: true,
      confirmButtonText: "Next",
      inputValidator: (value) => {
        if (!value) return "Please select a reason";
      },
    }).then((result) => {
      if (result.isConfirmed) {
        const reason = result.value;
        Swal.fire({
          title: "Enter Remark",
          input: "text",
          inputPlaceholder: "Remark (optional)",
          showCancelButton: true,
          confirmButtonText: "Cancel IRN",
          confirmButtonColor: "#dc2626",
        }).then((remarkResult) => {
          if (remarkResult.isConfirmed) {
            mutateCancelIRN({
              url: "/admin/booking/cancelIRN",
              data: { invoice_id: invoiceId, reason, remark: remarkResult.value || "" },
            });
          }
        });
      }
    });
  };

  const loadChangeLog = () => {
    const awbId = awbTrackingData?.awbData?.id;
    if (!awbId) {
      UseToast("AWB not loaded yet", "error");
      return;
    }

    const pickupId = awbTrackingData?.bookingData?.pickup_id || awbTrackingData?.bookingData?.pickup_code;
    mutateChangeLog({ awbId: String(awbId), pickupId: pickupId ? String(pickupId) : undefined });
  };

  return (
    <>
      {/* Hidden file inputs for slip/proof upload */}
      <input
        type="file"
        accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
        ref={slipInputRef}
        className="hidden"
        onChange={(e) => {
          if (e.target.files?.[0]) handleSlipUpload(e.target.files[0]);
          e.target.value = "";
        }}
      />
      <input
        type="file"
        accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
        ref={proofUploadInputRef}
        className="hidden"
        onChange={(e) => {
          if (e.target.files?.[0]) handleProofUpload(e.target.files[0]);
          e.target.value = "";
        }}
      />

      {/* Proof Gallery Popup */}
      {proofPopup && (
        <div className="fixed inset-0 bg-black/50 z-[9999] flex items-center justify-center p-4 sm:p-2" onClick={() => setProofPopup(null)}>
          <div
            className="rounded-2xl shadow-xl w-full max-w-2xl max-h-[85vh] sm:max-h-[90vh] overflow-y-auto bg-white p-5 sm:p-3"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-base font-bold text-slate-800">
                AWB Proofs — <span className="text-primary">{awbTrackingData?.awbData?.awb_no}</span>
              </h2>
              <button className="text-slate-400 hover:text-slate-700" onClick={() => setProofPopup(null)}>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <button
              type="button"
              className="mb-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-600 text-white text-xs font-semibold hover:bg-blue-700 transition-colors"
              onClick={() => proofUploadInputRef.current?.click()}
              disabled={isUploading}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
                />
              </svg>
              {isUploading ? "Uploading..." : "Upload Proof"}
            </button>
            {getProofUrls(awbTrackingData?.awbData?.pickImgs).length > 0 ? (
              <div className="grid grid-cols-3 sm:grid-cols-2 gap-3 sm:gap-2">
                {getProofUrls(awbTrackingData?.awbData?.pickImgs).map((url: string, idx: number) => (
                  <div key={idx} className="relative group rounded-xl overflow-hidden border border-slate-200 bg-slate-50">
                    <img
                      src={url}
                      alt={`Proof ${idx + 1}`}
                      className="w-full h-32 object-cover cursor-pointer"
                      onClick={() => window.open(url, "_blank")}
                    />
                    <button
                      type="button"
                      className="absolute top-1 right-1 w-6 h-6 rounded-full bg-red-500 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity text-xs"
                      onClick={() => handleDeleteImage("proof", url)}
                      disabled={isDeleting}
                      title="Delete"
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-slate-400 text-center py-8">No proofs uploaded yet</p>
            )}
          </div>
        </div>
      )}

      {isLabelOpen && (
        <AwbLabel
          isLabelOpen={isLabelOpen}
          setIsLabelOpen={setIsLabelOpen}
          awbData={{ ...awbTrackingData?.awbData, _assign_to_booking: awbTrackingData?.bookingData }}
          setAwbData={() => {}}
        />
      )}
      {showDataLogger && (
        <div className="fixed inset-0 bg-black-900 bg-opacity-20 flex items-center justify-center z-50 p-4 sm:p-3">
          <div className="rounded-xl border-2 border-primary p-4 sm:p-3 bg-white relative w-full max-w-md sm:max-w-[95vw]">
            <div className="flex items-center justify-between bg-blue-600 text-white p-2 rounded-lg">
              <p>Data Logger — {awbNo}</p>
              <button className="px-1" onClick={() => setShowDataLogger(false)}>
                ✖
              </button>
            </div>
            <div className="mt-4 flex flex-col gap-3">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Device Company</label>
                <input
                  type="text"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  value={dlDeviceCompany}
                  onChange={(e) => setDlDeviceCompany(e.target.value)}
                  placeholder="Enter device company"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Device No</label>
                <input
                  type="text"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  value={dlDeviceNo}
                  onChange={(e) => setDlDeviceNo(e.target.value)}
                  placeholder="Enter device number"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">File</label>
                <input
                  type="file"
                  ref={dlFileRef}
                  accept="image/*,.pdf"
                  className="w-full text-sm text-gray-500 file:mr-2 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                  onChange={(e) => setDlFile(e.target.files?.[0] || null)}
                />
                {getDataLoggerLog().file && !dlFile && (
                  <a
                    href={get_file_url(getDataLoggerLog().file)}
                    target="_blank"
                    rel="noreferrer"
                    className="text-xs text-blue-600 underline mt-1 inline-block"
                  >
                    View existing file
                  </a>
                )}
              </div>
              {(() => {
                const dl = getDataLoggerLog();
                const companyLower = (dlDeviceCompany || dl.deviceCompany || "").replaceAll(" ", "_").trim().toLowerCase();
                const deviceNoLower = (dlDeviceNo || dl.deviceNo || "").replaceAll(" ", "_").trim().toLowerCase();
                const calibrationUrl =
                  companyLower && deviceNoLower
                    ? `https://api.airfield.express/logger-cert/${companyLower}-${deviceNoLower}.pdf`
                    : null;

                return calibrationUrl ? (
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Calibration Report</label>
                    <a
                      href={calibrationUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded-md text-xs font-medium transition duration-150 shadow-sm"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      View Calibration Certificate
                    </a>
                  </div>
                ) : null;
              })()}
              <Button
                className="mt-1 w-full bg-blue-600 text-white rounded-lg"
                isDisabled={isDlSubmitting}
                onPress={() => {
                  mutateDataLogger({
                    id: awbTrackingData?.awbData?.id,
                    deviceCompany: dlDeviceCompany,
                    deviceNo: dlDeviceNo,
                    file: dlFile,
                  });
                }}
              >
                {isDlSubmitting ? "Saving..." : "Save"}
              </Button>
            </div>
          </div>
        </div>
      )}
      {/* ═══════════ E-Way Bill Popup ═══════════ */}
      {showEwb && (
        <div className="fixed inset-0 bg-black/50 z-[9999] flex items-center justify-center p-4 sm:p-2" onClick={() => setShowEwb(false)}>
          <div
            className="rounded-2xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto bg-white font-inter"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header with EWB No inline + actions */}
            <div className="flex items-center gap-3 flex-wrap bg-emerald-600 text-white px-4 py-2 rounded-t-2xl">
              <span className="font-bold text-sm mr-1">E-Way Bill</span>
              <span className="bg-white/20 px-2.5 py-0.5 rounded-lg text-xs font-bold tracking-wide">{ewbNo}</span>
              {ewbData?.status && (
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                  ewbData.status === "ACT" ? "bg-green-300 text-green-900" :
                  ewbData.status === "CNL" ? "bg-red-300 text-red-900" :
                  "bg-yellow-300 text-yellow-900"
                }`}>
                  {ewbData.status === "ACT" ? "Active" : ewbData.status === "CNL" ? "Cancelled" : "Expired"}
                </span>
              )}
              <div className="ml-auto flex items-center gap-1.5">
                <button className="bg-white/20 hover:bg-white/30 text-white text-[10px] font-semibold px-2 py-1 rounded-lg transition-colors" onClick={handleEwbFetch} disabled={isEwbLoading}>
                  {isEwbLoading ? "..." : "↻ Refresh"}
                </button>
                <button className="bg-red-500 hover:bg-red-600 text-white text-[10px] font-semibold px-2 py-1 rounded-lg transition-colors" onClick={handleEwbPdfDownload} disabled={isEwbPdfLoading || !ewbNo}>
                  {isEwbPdfLoading ? "..." : "↓ PDF"}
                </button>
                <button className="text-white/80 hover:text-white ml-1" onClick={() => setShowEwb(false)}>✕</button>
              </div>
            </div>

            {/* Tabs */}
            <div className="flex gap-0 border-b border-slate-200 bg-slate-50 px-2">
              {([
                { key: "details", label: "Details" },
                { key: "partb", label: "Part-B (Vehicle)" },
                { key: "transporter", label: "Transporter" },
                { key: "history", label: "History" },
              ] as const).map((t) => (
                <button
                  key={t.key}
                  className={`px-3 py-1.5 text-[11px] font-semibold border-b-2 transition-colors ${
                    ewbTab === t.key
                      ? "border-emerald-600 text-emerald-700 bg-white"
                      : "border-transparent text-slate-500 hover:text-slate-700"
                  }`}
                  onClick={() => setEwbTab(t.key)}
                >
                  {t.label}
                </button>
              ))}
            </div>

            <div className="p-3">
              {/* ── Details Tab ── */}
              {ewbTab === "details" && (
                <div>
                  {ewbData ? (
                    <div className="space-y-2">
                      {/* Row 1: Key info inline */}
                      <div className="flex flex-wrap gap-x-5 gap-y-1 text-xs bg-slate-50 rounded-lg px-3 py-2 border border-slate-200">
                        <span className="text-slate-500">EWB Date: <b className="text-slate-800">{ewbData.ewbDate}</b></span>
                        <span className="text-slate-500">Valid Upto: <b className="text-slate-800">{ewbData.validUpto}</b></span>
                        <span className="text-slate-500">Doc No: <b className="text-slate-800">{ewbData.docNo}</b></span>
                        <span className="text-slate-500">Distance: <b className="text-slate-800">{ewbData.transDistance} km</b></span>
                      </div>

                      {/* Row 2: From → To */}
                      <div className="grid grid-cols-2 sm:grid-cols-1 gap-2">
                        <div className="bg-blue-50 rounded-lg px-3 py-2 border border-blue-200">
                          <p className="text-[10px] text-blue-400 font-bold uppercase">From</p>
                          <p className="text-xs font-bold text-blue-800 leading-tight">{ewbData.fromTrdName}</p>
                          <p className="text-[11px] text-blue-600 leading-tight">{ewbData.fromAddr1}{ewbData.fromAddr2 ? `, ${ewbData.fromAddr2}` : ""}, {ewbData.fromPlace} - {ewbData.fromPincode}</p>
                          <p className="text-[10px] text-blue-500">GSTIN: {ewbData.fromGstin}</p>
                        </div>
                        <div className="bg-indigo-50 rounded-lg px-3 py-2 border border-indigo-200">
                          <p className="text-[10px] text-indigo-400 font-bold uppercase">To</p>
                          <p className="text-xs font-bold text-indigo-800 leading-tight">{ewbData.toTrdName}</p>
                          <p className="text-[11px] text-indigo-600 leading-tight">{ewbData.toAddr1}{ewbData.toAddr2 ? `, ${ewbData.toAddr2}` : ""}, {ewbData.toPlace} - {ewbData.toPincode}</p>
                          <p className="text-[10px] text-indigo-500">GSTIN: {ewbData.toGstin}</p>
                        </div>
                      </div>

                      {/* Row 3: Values + Transport inline */}
                      <div className="flex flex-wrap gap-x-5 gap-y-1 text-xs bg-white rounded-lg px-3 py-2 border border-slate-200">
                        <span className="text-slate-500">Total: <b className="text-slate-800">₹{ewbData.totalValue?.toLocaleString()}</b></span>
                        <span className="text-slate-500">Invoice: <b className="text-slate-800">₹{ewbData.totInvValue?.toLocaleString()}</b></span>
                        <span className="text-slate-500">IGST: <b className="text-slate-800">₹{ewbData.igstValue}</b></span>
                        <span className="text-slate-500">CGST: <b className="text-slate-800">₹{ewbData.cgstValue}</b></span>
                        <span className="text-slate-500">SGST: <b className="text-slate-800">₹{ewbData.sgstValue}</b></span>
                      </div>
                      <div className="flex flex-wrap gap-x-5 gap-y-1 text-xs bg-white rounded-lg px-3 py-2 border border-slate-200">
                        <span className="text-slate-500">Transporter: <b className="text-slate-800">{ewbData.transporterName || "-"}</b> <span className="text-slate-400">({ewbData.transporterId || "-"})</span></span>
                        <span className="text-slate-500">Vehicle: <b className="text-slate-800">{ewbData.vehicleNo || "-"}</b></span>
                        <span className="text-slate-500">Mode: <b className="text-slate-800">{ewbData.transMode === "1" ? "Road" : ewbData.transMode === "2" ? "Rail" : ewbData.transMode === "3" ? "Air" : "Ship"}</b></span>
                        <span className="text-slate-500">Type: <b className="text-slate-800">{ewbData.vehicleType === "R" ? "Regular" : "ODC"}</b></span>
                      </div>

                      {/* Items table compact */}
                      {ewbData.itemList?.length > 0 && (
                        <div className="overflow-x-auto">
                          <table className="w-full text-[11px] border border-slate-200 rounded">
                            <thead>
                              <tr className="bg-slate-50 text-slate-500 uppercase text-[9px]">
                                <th className="px-2 py-1 text-left">#</th>
                                <th className="px-2 py-1 text-left">Product</th>
                                <th className="px-2 py-1 text-left">HSN</th>
                                <th className="px-2 py-1 text-right">Qty</th>
                                <th className="px-2 py-1 text-right">Taxable</th>
                                <th className="px-2 py-1 text-right">IGST%</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                              {ewbData.itemList.map((item: any, idx: number) => (
                                <tr key={idx}>
                                  <td className="px-2 py-1">{item.itemNo}</td>
                                  <td className="px-2 py-1">{item.productName}</td>
                                  <td className="px-2 py-1">{item.hsnCode}</td>
                                  <td className="px-2 py-1 text-right">{item.quantity} {item.qtyUnit}</td>
                                  <td className="px-2 py-1 text-right">₹{item.taxableAmount?.toLocaleString()}</td>
                                  <td className="px-2 py-1 text-right">{item.igstRate}%</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  ) : (
                    <p className="text-xs text-slate-400 text-center py-6">
                      {isEwbLoading ? "Loading..." : "No data — click Refresh"}
                    </p>
                  )}
                </div>
              )}

              {/* ── Part-B (Vehicle) Tab ── */}
              {ewbTab === "partb" && (
                <div className="space-y-2">
                  <div className="grid grid-cols-3 sm:grid-cols-2 gap-2">
                    <div>
                      <label className="block text-[10px] font-semibold text-gray-500 uppercase mb-0.5">
                        {ewbTransMode === "1" ? "Vehicle No *" : "Vehicle No"}
                      </label>
                      <input
                        type="text"
                        className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-xs focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500"
                        value={ewbVehicleNo}
                        onChange={(e) => setEwbVehicleNo(e.target.value.toUpperCase())}
                        placeholder={ewbTransMode === "1" ? "MH12AB1234" : "Optional for Air/Rail/Ship"}
                        disabled={ewbTransMode !== "1"}
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] font-semibold text-gray-500 uppercase mb-0.5">From Place</label>
                      <input type="text" className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-xs focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500" value={ewbFromPlace} onChange={(e) => setEwbFromPlace(e.target.value)} placeholder="City" />
                    </div>
                    <div>
                      <label className="block text-[10px] font-semibold text-gray-500 uppercase mb-0.5">State Code</label>
                      <input type="text" className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-xs focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500" value={ewbFromState} onChange={(e) => setEwbFromState(e.target.value)} placeholder="29" />
                    </div>
                    <div>
                      <label className="block text-[10px] font-semibold text-gray-500 uppercase mb-0.5">Reason</label>
                      <select className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-xs focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500" value={ewbReasonCode} onChange={(e) => setEwbReasonCode(e.target.value)}>
                        <option value="1">Break Down</option>
                        <option value="2">Transhipment</option>
                        <option value="3">Others</option>
                        <option value="4">First Time</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-[10px] font-semibold text-gray-500 uppercase mb-0.5">Remark</label>
                      <input type="text" className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-xs focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500" value={ewbReasonRem} onChange={(e) => setEwbReasonRem(e.target.value)} placeholder="Optional" />
                    </div>
                    <div>
                      <label className="block text-[10px] font-semibold text-gray-500 uppercase mb-0.5">Doc No</label>
                      <input type="text" className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-xs focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500" value={ewbTransDocNo} onChange={(e) => setEwbTransDocNo(e.target.value)} placeholder="Doc no" />
                    </div>
                    <div>
                      <label className="block text-[10px] font-semibold text-gray-500 uppercase mb-0.5">Doc Date</label>
                      <input type="text" className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-xs focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500" value={ewbTransDocDate} onChange={(e) => setEwbTransDocDate(e.target.value)} placeholder="DD/MM/YYYY" />
                    </div>
                    <div>
                      <label className="block text-[10px] font-semibold text-gray-500 uppercase mb-0.5">Mode</label>
                      <select className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-xs focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500" value={ewbTransMode} onChange={(e) => setEwbTransMode(e.target.value)}>
                        <option value="1">Road</option>
                        <option value="2">Rail</option>
                        <option value="3">Air</option>
                        <option value="4">Ship</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-[10px] font-semibold text-gray-500 uppercase mb-0.5">Vehicle Type</label>
                      <select className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-xs focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500" value={ewbVehicleType} onChange={(e) => setEwbVehicleType(e.target.value)}>
                        <option value="R">Regular</option>
                        <option value="O">ODC</option>
                      </select>
                    </div>
                  </div>
                  <Button size="sm" className="w-full bg-emerald-600 text-white rounded-lg text-xs font-semibold" isDisabled={isEwbPartBLoading} onPress={handleEwbPartBSubmit}>
                    {isEwbPartBLoading ? "Updating..." : "Update Vehicle (Part-B)"}
                  </Button>
                </div>
              )}

              {/* ── Transporter Tab ── */}
              {ewbTab === "transporter" && (
                <div className="space-y-2">
                  {ewbData?.transporterId && (
                    <p className="text-xs text-slate-500 bg-slate-50 rounded-lg px-3 py-1.5 border border-slate-200">
                      Current: <b className="text-slate-700">{ewbData.transporterName}</b> ({ewbData.transporterId})
                    </p>
                  )}
                  <div className="flex gap-2 items-end">
                    <div className="flex-1">
                      <label className="block text-[10px] font-semibold text-gray-500 uppercase mb-0.5">New Transporter GSTIN *</label>
                      <input type="text" className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-xs focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500" value={ewbTransporterId} onChange={(e) => setEwbTransporterId(e.target.value.toUpperCase())} placeholder="Transporter GSTIN" />
                    </div>
                    <Button size="sm" className="bg-emerald-600 text-white rounded-lg text-xs font-semibold px-4" isDisabled={isEwbTransporterLoading} onPress={handleEwbTransporterSubmit}>
                      {isEwbTransporterLoading ? "..." : "Update"}
                    </Button>
                  </div>
                </div>
              )}

              {/* ── History Tab ── */}
              {ewbTab === "history" && (
                <div>
                  {isEwbHistoryLoading ? (
                    <p className="text-xs text-slate-400 text-center py-4">Loading...</p>
                  ) : ewbHistory.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="w-full text-[11px] border border-slate-200 rounded">
                        <thead>
                          <tr className="bg-slate-50 text-slate-500 uppercase text-[9px]">
                            <th className="px-2 py-1 text-left">Date</th>
                            <th className="px-2 py-1 text-left">Action</th>
                            <th className="px-2 py-1 text-left">Vehicle</th>
                            <th className="px-2 py-1 text-left">Transporter</th>
                            <th className="px-2 py-1 text-left">Mode</th>
                            <th className="px-2 py-1 text-left">Reason</th>
                            <th className="px-2 py-1 text-left">Status</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {ewbHistory.map((h: any, idx: number) => (
                            <tr key={idx}>
                              <td className="px-2 py-1 whitespace-nowrap">{h.createdAt || "-"}</td>
                              <td className="px-2 py-1">
                                <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold ${h.action === "UPDATE_VEHICLE" ? "bg-blue-100 text-blue-700" : "bg-purple-100 text-purple-700"}`}>
                                  {h.action === "UPDATE_VEHICLE" ? "Vehicle" : "Transporter"}
                                </span>
                              </td>
                              <td className="px-2 py-1 font-semibold">{h.vehicleNo || "-"}</td>
                              <td className="px-2 py-1">{h.transporterId || "-"}</td>
                              <td className="px-2 py-1">{h.transMode === "1" ? "Road" : h.transMode === "2" ? "Rail" : h.transMode === "3" ? "Air" : h.transMode === "4" ? "Ship" : "-"}</td>
                              <td className="px-2 py-1">{h.reasonRem || "-"}</td>
                              <td className="px-2 py-1">
                                <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold ${h.status === "SUCCESS" ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>{h.status}</span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="text-xs text-slate-400 text-center py-4">No history</p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {showPayPoup && (
        <div className="fixed inset-0 bg-black-900 bg-opacity-20 flex items-center justify-center z-50 p-4 sm:p-3">
          <div className="rounded-xl border-2 border-primary p-4 sm:p-3 bg-white relative w-full max-w-fit sm:max-w-[95vw]">
            <div className="flex items-center justify-between bg-green-600 text-white p-2 rounded-lg">
              <p>Payment</p>
              <button
                className="px-1"
                onClick={() => {
                  setShowPayPoup(false);
                }}
              >
                ✖
              </button>
            </div>

            <div className="mt-4">
              <div className="flex flex-row sm:flex-col gap-0 sm:gap-3">
                <div className="w-[200px] sm:w-full h-[250px] sm:h-[200px] border rounded-md mr-3 sm:mr-0 p-2">
                  {isQrLoading ? (
                    <span>Loading....</span>
                  ) : !paymentLink ? (
                    <span>Failed To Load</span>
                  ) : (
                    <div>
                      <QRCode
                        value={paymentLink}
                        className="w-full h-full mb-4"
                      />
                      <Button
                        className="mt-3 bg-green-600 mx-auto px-2 py-1 rounded-md text-white flex items-center justify-between"
                        onPress={checkPayStatus}
                        isDisabled={isCheckPayLoading}
                      >
                        {isCheckPayLoading ? "Checking..." : "Check Status"}
                      </Button>
                    </div>
                  )}
                </div>

                <div className="">
                  <input
                    value={awbTrackingData?.awbData?._invoice_no_c_b?.total_value}
                    disabled
                    type="text"
                    placeholder="amount"
                    className="border rounded-md focus:border-black-900 bg-gray-200"
                  />
                  <br />
                  <input
                    value={payRemark}
                    onChange={(e) => {
                      setPayRemark(e.target.value);
                    }}
                    type="text"
                    placeholder="remark"
                    className="mt-3 border rounded-md focus:border-black-900"
                  />
                  <br />
                  <Button
                    type="button"
                    className="mt-3 bg-green-600 px-2 py-1 rounded-md text-white flex items-center justify-between"
                    onPress={handleManualPayButton}
                  >
                    {<CurrencyRupeeIcon className="h-6 mr-1" />} Pay
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {showVolume && (
        <div className="fixed inset-0 bg-black-900 bg-opacity-20 flex items-center justify-center z-20 p-4 sm:p-3">
          <div className="rounded-xl border-2 border-primary p-4 sm:p-3 bg-white relative w-full max-w-fit sm:max-w-[95vw] overflow-x-auto">
            <div className="flex items-center justify-between bg-primary text-white p-2 rounded-lg">
              <p>Total Weight: {awbTrackingData?.awbData?.weight}</p>
              <button
                className="px-1"
                onClick={() => {
                  setShowVolume(false);
                }}
              >
                ✖
              </button>
            </div>

            <div>
              <table className="table mt-2">
                <tr>
                  <th className="px-2 border-r">Sr.</th>
                  <th className="px-2">Lenght</th>
                  <th className="px-2">Width</th>
                  <th className="px-2">Height</th>
                  <th className="px-2">Weight</th>
                </tr>
                {awbTrackingData?.awbData?.innerItemsJson && awbTrackingData?.awbData?.innerItemsJson != "[]" ? (
                  JSON.parse(awbTrackingData?.awbData?.innerItemsJson).map((i, a) => {
                    return (
                      <tr className="border-t">
                        <td className="px-4 border-r">{a + 1}</td>
                        <td className="px-4">{i.l}</td>
                        <td className="px-4">{i.b}</td>
                        <td className="px-4">{i.h}</td>
                        <td className="px-4">{i.weight}</td>
                      </tr>
                    );
                  })
                ) : (
                  <tr className="border-t">
                    <td className="px-4">1</td>
                    <td className="px-4">{awbTrackingData?.awbData?.l}</td>
                    <td className="px-4">{awbTrackingData?.awbData?.b}</td>
                    <td className="px-4">{awbTrackingData?.awbData?.h}</td>
                    <td className="px-4">{awbTrackingData?.awbData?.weight}</td>
                  </tr>
                )}
              </table>
            </div>
          </div>
        </div>
      )}
      <div className="bg-gradient-to-br from-purple-400 via-blue-50 to-primary/50 py-2 px-2 font-inter flex-1 min-h-screen">
        {/* ═══════════ ROW 1: TITLE + ALL ACTION BUTTONS ═══════════ */}
        <div className="mx-auto mb-2">
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl px-3 py-2 shadow-xl shadow-slate-200/50">
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="text-base font-bold bg-gradient-to-r from-primary to-amber-600 bg-clip-text text-transparent uppercase mr-auto">
                AWB Tracking
              </h1>
              {awbTrackingData?.awbData?.awb_no && (
                <div className="flex items-center gap-1.5 flex-wrap">
                  <Button
                    size="sm"
                    className="bg-blue-600 text-white border border-blue-200 text-xs font-semibold hover:bg-blue-700 transition-colors"
                    onPress={() => setIsLabelOpen(true)}
                  >
                    Label
                  </Button>
                  {canEditBook && (
                    <Button
                      size="sm"
                      className="bg-amber-50 text-amber-700 border border-amber-200 text-xs font-semibold hover:bg-amber-100 transition-colors"
                      onPress={() => {
                        navigator("/booking/add", {
                          state: { mode: "edit", data: awbTrackingData.bookingData },
                        });
                      }}
                    >
                      Edit
                    </Button>
                  )}
                  <Button
                    size="sm"
                    className="bg-violet-50 text-violet-700 border border-violet-200 text-xs font-semibold hover:bg-violet-100 transition-colors"
                    onPress={() => {
                      const width = 1200;
                      const height = 800;
                      const left = (window.screen.width - width) / 2;
                      const top = (window.screen.height - height) / 2;
                      window.open(
                        `/mail/view?pickup_code=${awbTrackingData?.bookingData?.pickup_code}`,
                        "MailWindow",
                        `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`,
                      );
                    }}
                  >
                    Mail
                  </Button>
                  <Button
                    size="sm"
                    className="bg-cyan-50 text-cyan-700 border border-cyan-200 text-xs font-semibold hover:bg-cyan-100 transition-colors"
                    onPress={openDataLoggerPopup}
                  >
                    Data Logger
                  </Button>
                  <Button
                    size="sm"
                    className="bg-indigo-50 text-indigo-700 border border-indigo-200 text-xs font-semibold hover:bg-indigo-100 transition-colors"
                    onPress={() => {
                      if (awbNo) {
                        navigator(`/operation/box-tracker?tab=packing&awb=${encodeURIComponent(awbNo)}`);
                      } else {
                        navigator(`/operation/box-tracker`);
                      }
                    }}
                  >
                    Box Tracker
                  </Button>
                  <Button
                    size="sm"
                    className="bg-emerald-50 text-emerald-700 border border-emerald-200 text-xs font-semibold hover:bg-emerald-100 transition-colors"
                    onPress={openEwbPopup}
                    isDisabled={!awbTrackingData?.bookingData?.ewayBillNo}
                  >
                    E-Way Bill
                  </Button>
                  <Button
                    size="sm"
                    className="bg-teal-50 text-teal-700 border border-teal-200 text-xs font-semibold hover:bg-teal-100 transition-colors"
                    onPress={() => setProofPopup(true)}
                  >
                    Sample Temp ({getProofUrls(awbTrackingData?.awbData?.pickImgs).length})
                  </Button>
                  <Button
                    size="sm"
                    className="bg-orange-50 text-orange-700 border border-orange-200 text-xs font-semibold hover:bg-orange-100 transition-colors"
                    onPress={handleClickComment}
                  >
                    Add Comment
                  </Button>
                  {userRole === roles.SUPER_ADMIN && !awbTrackingData?.bookingData?.customer_code && awbTrackingData?.awbData?._invoice_no_c_b && (
                    <Button
                      size="sm"
                      className="bg-rose-50 text-rose-700 border border-rose-200 text-xs font-semibold hover:bg-rose-100 transition-colors"
                      onPress={handleRegenInvoice}
                      isDisabled={isRegenLoading}
                    >
                      {isRegenLoading ? "Regenerating..." : "Regen Invoice"}
                    </Button>
                  )}
                  {userRole === roles.SUPER_ADMIN && awbTrackingData?.awbData?._invoice_no_c_b && (
                    <Button
                      size="sm"
                      className="bg-red-50 text-red-700 border border-red-200 text-xs font-semibold hover:bg-red-100 transition-colors"
                      onPress={handleCancelIRN}
                      isDisabled={isCancelIRNLoading}
                    >
                      {isCancelIRNLoading ? "Cancelling..." : "Cancel IRN"}
                    </Button>
                  )}
                  {!awbTrackingData?.bookingData?.customer_code && (
                    <Button
                      size="sm"
                      className="bg-green-600 hover:bg-green-700 text-white text-xs font-semibold flex items-center gap-1 transition-colors"
                      onPress={handlePayButton}
                    >
                      <CurrencyRupeeIcon className="h-3.5 w-3.5" /> Pay
                    </Button>
                  )}

                  {/* WhatsApp Send */}
                  {!awbTrackingData?.bookingData?.customer_code && (
                    <>
                      <div className="w-px h-5 bg-slate-300 mx-1"></div>
                      <input
                        placeholder="Mobile No."
                        type="text"
                        value={wappNo}
                        onChange={(e) => setWappNo(e.target.value)}
                        className="px-2 py-1 rounded-lg border border-slate-200 text-xs w-28 focus:ring-2 focus:ring-green-500/20 focus:border-green-500 outline-none transition-all"
                      />
                      <Button
                        size="sm"
                        className="bg-green-600 hover:bg-green-700 text-white text-xs font-semibold flex items-center gap-1 transition-colors"
                        onPress={() => SEND_WAPP(wappNo, "", awbTrackingData?.awbData?.awb_no)}
                      >
                        <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
                        </svg>
                        WhatsApp
                      </Button>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ═══════════ ROW 2: AWB INFO BADGES (left) + SLIPS & LINKS (right) ═══════════ */}
        {awbTrackingData?.awbData?.awb_no && (
          <div className="mx-auto mb-2">
            <div className="shadow-xl shadow-slate-200/50 bg-white/80 backdrop-blur-sm overflow-visible rounded-xl">
              <div className="px-3 py-2">
                <div className="flex items-center justify-between gap-4 flex-wrap">
                  {/* Left: AWB badges */}
                  <div className="flex items-center gap-3 flex-wrap">
                    <div>
                      <p className="text-[10px] text-slate-400 font-medium uppercase tracking-wider mb-0.5">Airway Bill No</p>
                      <span className="text-white bg-gradient-to-r from-blue-600 to-blue-700 font-bold inline-block px-4 py-1.5 rounded-xl text-sm shadow-md tracking-wide">
                        {awbTrackingData.awbData.awb_no}
                      </span>
                    </div>
                    <div>
                      <p className="text-[10px] text-slate-400 font-medium uppercase tracking-wider mb-0.5">Booking Date</p>
                      <span className="inline-flex items-center px-3 py-1.5 rounded-xl font-bold bg-slate-100 text-slate-700 border border-slate-200">
                        {moment(awbTrackingData.bookingData?.date, "DD-MM-YYYY HH:mm:ss").format("DD-MM-YYYY")}
                      </span>
                    </div>
                    {awbTrackingData.tracking_data.length > 0 && (
                      <div>
                        <p className="text-[10px] text-slate-400 font-medium uppercase tracking-wider mb-0.5">Current Status</p>
                        <span className="inline-flex items-center px-2 py-0.5 rounded-xl text-sm font-bold bg-indigo-100 text-indigo-800 border border-indigo-200">
                          {awbTrackingData.tracking_data[awbTrackingData.tracking_data.length - 1]?.message_code}
                        </span>
                      </div>
                    )}
                    {!awbTrackingData?.bookingData?.customer_code && awbTrackingData?.awbData?._invoice_no_c_b && (
                      <div>
                        <p className="text-[10px] text-slate-400 font-medium uppercase tracking-wider mb-0.5">Payment</p>
                        {awbTrackingData?.awbData?._invoice_no_c_b?.isFullPaid ? (
                          <span className="inline-flex items-center px-3 py-1.5 rounded-xl text-xs font-bold bg-green-100 text-green-700 border border-green-200">
                            PAID ({awbTrackingData?.awbData?._invoice_no_c_b?.payStatus == "CASH" ? "CASH" : "UPI"})
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-3 py-1.5 rounded-xl text-xs font-bold bg-red-50 text-red-600 border border-red-200">
                            {awbTrackingData?.awbData?._invoice_no_c_b?.payStatus || "PENDING"}
                          </span>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Right: Slips & Links */}
                  <div className="flex items-center gap-2.5 flex-wrap">
                    {awbTrackingData.awbData?.invoice_no_c_b && (
                      <Link
                        isBlock
                        showAnchorIcon
                        color="primary"
                        target="_blank"
                        rel="noreferrer"
                        href={`${process.env.REACT_APP_BACKEND_URL}/invoices/${awbTrackingData.awbData.subbranch}/${awbTrackingData.awbData._invoice_no_c_b.is_proforma ? ("proforma_"+awbTrackingData.awbData._invoice_no_c_b?.id) : (awbTrackingData.awbData._invoice_no_c_b?.invoice_no || "")}.pdf`}
                        className="text-sm whitespace-nowrap border-2 border-primary rounded-full hover:bg-blue-50 px-2 py-0.5 transition-colors"
                        underline="hover"
                      >
                        {awbTrackingData.awbData._invoice_no_c_b.is_proforma ? "Proforma Invoice" : "Invoice"}
                      </Link>
                    )}
                    <Link
                      isBlock
                      showAnchorIcon
                      color="primary"
                      target="_blank"
                      rel="noreferrer"
                      href={`/awb/slip?awb=${awbTrackingData.awbData.awb_no}`}
                      className="text-sm whitespace-nowrap border-2 border-primary rounded-full hover:bg-blue-50 px-2 py-0.5 transition-colors"
                      underline="hover"
                    >
                      Cgnee Slip
                    </Link>
                    {(userRole == "Admin" || userRole == "CUSTOMER") && (
                      <Link
                        isBlock
                        showAnchorIcon
                        color="primary"
                        target="_blank"
                        rel="noreferrer"
                        href={`/awb/slip?awb=${awbTrackingData.awbData.awb_no}&sh=1`}
                        className="text-sm whitespace-nowrap border-2 border-primary rounded-full hover:bg-blue-50 px-2 py-0.5 transition-colors"
                        underline="hover"
                      >
                        Shipper Slip
                      </Link>
                    )}
                    <div className="w-px h-4 bg-slate-300"></div>
                    <Link
                      isBlock
                      showAnchorIcon
                      color={awbTrackingData?.awbData?.parcelPhoto ? "primary" : "foreground"}
                      target="_blank"
                      rel="noreferrer"
                      isDisabled={!awbTrackingData?.awbData?.parcelPhoto}
                      href={awbTrackingData?.awbData?.parcelPhoto && get_file_url(awbTrackingData?.awbData?.parcelPhoto || "#")}
                      className="text-xs whitespace-nowrap"
                      underline="hover"
                    >
                      Shipment Photo
                    </Link>
                    {awbTrackingData.awbData.slipAwbData ? (
                      awbTrackingData.awbData.slipAwbData.map((v, i) => (
                        <a
                          key={i}
                          target="_blank"
                          href={`${get_file_url(v.slip_url)}`}
                          className="text-xs text-blue-600 hover:text-blue-800 hover:underline transition-colors"
                        >
                          AWB Slip {i + 1}
                        </a>
                      ))
                    ) : (
                      <span className="text-xs text-slate-400">No AWB Slips</span>
                    )}
                    {awbTrackingData.awbData.slipMbagData?.length ? (
                      awbTrackingData.awbData.slipMbagData.map((v, i) => (
                        <a
                          key={i}
                          target="_blank"
                          href={`${get_file_url(v.slip_url)}`}
                          className="text-xs text-blue-600 hover:text-blue-800 hover:underline transition-colors"
                        >
                          MBag Slip {i + 1}
                        </a>
                      ))
                    ) : (
                      <span className="text-xs text-slate-400">No MBag Slips</span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ═══════════ TWO-COLUMN INFO CARDS ═══════════ */}
        {awbTrackingData?.awbData?.awb_no && (
          <div className="grid grid-cols-2 md:grid-cols-1 gap-2 mx-auto mb-2">
            {/* ─── Section 1: Booking Details ─── */}
            <div className="shadow-xl shadow-slate-200/50 bg-white/80 backdrop-blur-sm rounded-2xl overflow-hidden">
              <div className="flex items-center gap-2 px-4 py-2 border-b border-slate-100 bg-gradient-to-r from-blue-50 to-white">
                <div className="w-1 h-4 bg-blue-600 rounded-full"></div>
                <h2 className="text-xs font-bold text-slate-700 uppercase tracking-wider">Booking Details</h2>
              </div>
              <div className="px-4 py-2">
                <table className="w-full text-sm">
                  <tbody>
                    {getTRline(
                      "Product",
                      productList.map((p) => (p.product_code == awbTrackingData.awbData.prefix ? p.product_name : "")).join("") || "-",
                    )}
                    {getTRline(
                      "Ref. Code",
                      <a
                        className="text-blue-600 hover:text-blue-800 underline underline-offset-2 transition-colors font-semibold"
                        target="_blank"
                        href={`/pickup/tracking?awb=${awbTrackingData.bookingData.pickup_code}`}
                      >
                        {awbTrackingData.bookingData.pickup_code}
                      </a>,
                      true,
                    )}
                    <tr className="text-sm">
                      <td className="text-right pr-3 py-2 whitespace-nowrap text-slate-500 font-medium align-top">City / Pincode</td>
                      <td className="py-2">
                        <div className="flex items-center gap-2">
                          <div className="text-center bg-slate-100 px-3 py-1.5 rounded-lg">
                            <b className="text-slate-800 ">{awbTrackingData.bookingData.shipper_city}</b>
                            <br />
                            <span className="text-slate-600 font-semibold">{awbTrackingData.bookingData.shipper_pincode}</span>
                          </div>
                          <span className="text-lg text-blue-500 font-bold">→</span>
                          <div className="text-center bg-blue-50 px-3 py-1.5 rounded-lg border border-blue-100">
                            <b className="text-blue-700">{awbTrackingData.awbData.destination}</b>
                            <br />
                            <span className="text-blue-600 font-semibold">{awbTrackingData.awbData.pincode}</span>
                          </div>
                        </div>
                      </td>
                    </tr>
                    {getTRline("Product Type", awbTrackingData.awbData.dox_or_non_dox ? "DOX" : "Non-Dox", true)}
                    {getTRline(
                      "Client Name",
                      <span className="font-semibold text-black-900">
                        {awbTrackingData.bookingData.customer_sh}{" "}
                        <span className="text-primary font-normal">({awbTrackingData.bookingData.customer_code})</span>
                      </span>,
                    )}
                    <tr className="text-sm bg-slate-50">
                      <td className="text-right pr-3 py-2 whitespace-nowrap text-slate-500 font-medium align-top">Shipper</td>
                      <td className="py-2">
                        <div className="bg-gradient-to-r from-slate-50 to-slate-100 p-2 rounded-lg border border-slate-200">
                          <b className="text-slate-800">{awbTrackingData.bookingData.shipper_sh}</b>
                          <p className="text-[13px] text-slate-500 mt-0.5 leading-tight">{awbTrackingData.bookingData.shipper_address}</p>
                        </div>
                      </td>
                    </tr>
                    <tr className="text-sm">
                      <td className="text-right pr-3 py-2 whitespace-nowrap text-slate-500 font-medium align-top">Consignee</td>
                      <td className="py-2">
                        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-2 rounded-lg border border-blue-100">
                          <b className="text-blue-800">{awbTrackingData.awbData.consignee}</b>
                          <p className="text-[13px] text-blue-600 mt-0.5 leading-tight">{awbTrackingData.awbData.address}</p>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* ─── Section 2: Delivery & Shipment Info (2-column inside) ─── */}
            <div className="shadow-xl shadow-slate-200/50 bg-white/80 backdrop-blur-sm rounded-2xl overflow-hidden">
              <div className="flex items-center gap-2 px-4 py-2 border-b border-slate-100 bg-gradient-to-r from-indigo-50 to-white">
                <div className="w-1 h-4 bg-indigo-600 rounded-full"></div>
                <h2 className="text-xs font-bold text-slate-700 uppercase tracking-wider">Delivery & Shipment Info</h2>
              </div>
              <div className="flex sm:flex-col gap-0 divide-x sm:divide-x-0 sm:divide-y divide-slate-100">
                {/* Col 1: Weight & Parameters */}
                <div className="flex-1 px-4 py-3 flex flex-col gap-3">
                  {awbTrackingData.awbData.realWeight && (
                    <div className="bg-white px-3 py-1.5 rounded-xl border border-slate-200 shadow-sm">
                      <p className="text-[10px] text-slate-400 font-semibold uppercase tracking-wider">Actual Wt.</p>
                      <p className="text-sm font-bold text-slate-700">{awbTrackingData.awbData.realWeight} kg</p>
                    </div>
                  )}
                  <div className="bg-blue-50 px-3 py-1.5 rounded-xl border border-blue-200">
                    <p className="text-[10px] text-blue-400 font-semibold uppercase tracking-wider">Chargeable Wt.</p>
                    <p className="text-sm font-bold text-blue-700">
                      {awbTrackingData.awbData.prefix != "I" && awbTrackingData.awbData.weight}
                      {awbTrackingData.awbData.prefix == "I" &&
                        getChargeableWeight(
                          awbTrackingData.awbData.realWeight,
                          awbTrackingData.awbData.prefix,
                          awbTrackingData.awbData.l,
                          awbTrackingData.awbData.b,
                          awbTrackingData.awbData.h,
                        )}{" "}
                      kg
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`px-2.5 py-1 rounded-full text-[14px] font-semibold bg-slate-100 text-slate-500 border border-slate-200`}>
                      10:30:{" "}
                      <span className={`font-semibold ${awbTrackingData.awbData.is10_30 ? "text-green-700" : "text-red-800"}`}>
                        {awbTrackingData.awbData.is10_30 ? "Yes" : "No"}
                      </span>
                    </span>
                    <span className={`px-2.5 py-1 rounded-full text-[14px] font-semibold bg-slate-100 text-slate-500 border border-slate-200`}>
                      Holiday:{" "}
                      <span className={`font-semibold ${awbTrackingData.awbData.holiday ? "text-green-700" : "text-red-800"}`}>
                        {awbTrackingData.awbData.holiday ? "Yes" : "No"}
                      </span>
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`px-2.5 py-1 rounded-full text-[14px] font-semibold bg-slate-100 text-slate-500 border border-slate-200`}>
                      COD:{" "}
                      <span className={`font-semibold ${awbTrackingData.awbData.isCod ? "text-green-700" : "text-red-800"}`}>
                        {awbTrackingData.awbData.isCod ? "Yes" : "No"}
                      </span>
                    </span>
                    <span className={`px-2.5 py-1 rounded-full text-[14px] font-semibold bg-slate-100 text-slate-500 border border-slate-200`}>
                      FOD:{" "}
                      <span className={`font-semibold ${awbTrackingData.awbData.isFod ? "text-green-700" : "text-red-800"}`}>
                        {awbTrackingData.awbData.isFod ? "Yes" : "No"}
                      </span>
                    </span>
                  </div>
                  <span className="px-2.5 py-1 rounded-full text-[13px] font-semibold bg-purple-50 text-purple-700 border border-purple-200 self-start">
                    Temp Range: {awbTrackingData?.bookingData?.tempRange || "-"}
                  </span>
                  <Button
                    size="sm"
                    className="bg-primary hover:bg-primary/90 text-white text-xs font-semibold transition-colors"
                    onPress={() => setShowVolume(true)}
                  >
                    Volume Weight
                  </Button>
                  <Button
                    size="sm"
                    className="bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold transition-colors"
                    onPress={() => setShowImg(!showImg)}
                  >
                    POD Images
                  </Button>
                </div>

                {/* Col 2: Delivery details */}
                <div className="flex-1 px-3 py-2">
                  <table className="w-full text-sm">
                    <tbody>
                      {getTRline("Receiver", `${deliveryDoneInfo.receiver || "-"} (${deliveryDoneInfo.receiver_relation || "-"})`)}
                      {getTRline("Recv. Mobile", deliveryDoneInfo.recv_mobile || "-", true)}
                      {getTRline("Delivery Date", deliveryDoneInfo.updatedAt || deliveryDoneInfo.createdAt || "-")}
                      {getTRline("Shipper Mob.", awbTrackingData.bookingData?.shipper_mobile || "-", true)}
                      {getTRline("Cgnee Mob.", awbTrackingData.awbData?.contact_no || "-")}
                      {getTRline("MAWB No", awbTrackingData?.awbData?._mother_bag_id?.mawb || "-", true)}
                      {getTRline("MBag No", awbTrackingData?.awbData?._mother_bag_id?.mother_bag_code || "-")}
                      {getTRline("TP Name", <b className="text-slate-700">{awbTrackingData?.awbData?.thirdPartyName || "-"}</b>, true)}
                      {getTRline("TP Ref.", <b>{awbTrackingData?.awbData?.thirdPartyRef || "-"}</b>)}
                      {getTRline("Del. Emp.", deliveryDoneInfo._addedBy?.name || "-", true)}
                      {(() => {
                        const dl = getDataLoggerLog();
                        const companyLower = (dl.deviceCompany || "").replaceAll(" ", "_").trim().toLowerCase();
                        const deviceNoLower = (dl.deviceNo || "").replaceAll(" ", "_").trim().toLowerCase();
                        const calibrationUrl =
                          companyLower && deviceNoLower
                            ? `https://api.airfield.express/logger-cert/${companyLower}-${deviceNoLower}.pdf`
                            : null;

                        let customFields: any = {};
                        try {
                          let cf = awbTrackingData?.awbData?.customFields;
                          if (cf) {
                            while (typeof cf === "string") { cf = JSON.parse(cf); }
                            customFields = cf;
                          }
                        } catch { customFields = {}; }
                        return (
                          <>
                            {getTRline("DataLogger Sr No", (dl.deviceCompany || "-") + " | " + (dl.deviceNo || "-"))}
                            {getTRline(
                              "DataLogger Report",
                              dl.file ? (
                                <a
                                  href={get_file_url(dl.file)}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="text-blue-600 hover:text-blue-800 underline transition-colors"
                                >
                                  View Report
                                </a>
                              ) : (
                                "-"
                              ),
                              true,
                            )}
                            {getTRline(
                              "Calibration Report",
                              calibrationUrl ? (
                                <a
                                  href={calibrationUrl}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="text-blue-600 hover:text-blue-800 underline transition-colors"
                                >
                                  View Certificate
                                </a>
                              ) : (
                                "-"
                              ),
                            )}
                            {customFields.mainAwb && getTRline("Main AWB", <a className="text-blue-600 hover:text-blue-800 underline transition-colors font-bold" href={`/pod/tracking?awb=${customFields.mainAwb}`}>{customFields.mainAwb}</a>, true)}
                            {customFields.codAwb && getTRline("COD AWB", <a className="text-blue-600 hover:text-blue-800 underline transition-colors font-bold" href={`/pod/tracking?awb=${customFields.codAwb}`}>{customFields.codAwb}</a>)}
                            {customFields.codMaxDays && getTRline("COD Max Days", <b className="text-slate-700">{customFields.codMaxDays}</b>, true)}
                          </>
                        );
                      })()}
                      {awbTrackingData.tracking_data[awbTrackingData.tracking_data.length - 1]?.message_code?.includes("RTO") &&
                        getTRline(
                          <b>RTO AWB</b>,
                          <a
                            className="text-blue-600 hover:text-blue-800 underline transition-colors"
                            href={`/pod/tracking?awb=${awbTrackingData.awbData?.returnAwb}`}
                          >
                            {awbTrackingData.awbData?.returnAwb}
                          </a>,
                        )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ═══════════ POD IMAGES ═══════════ */}
        {showImg && (
          <div className="mx-auto mb-2">
            <div className="shadow-xl shadow-slate-200/50 bg-white/80 backdrop-blur-sm rounded-2xl overflow-hidden">
              <div className="flex items-center gap-2 px-4 py-2 border-b border-slate-100 bg-gradient-to-r from-blue-50 to-white">
                <div className="w-1 h-4 bg-blue-600 rounded-full"></div>
                <h2 className="text-xs font-bold text-slate-700 uppercase tracking-wider">Delivery Images</h2>
              </div>
              <div className="flex flex-row md:flex-col gap-4 md:gap-3 p-4">
                {awbTrackingData.tracking_data[awbTrackingData.tracking_data.length - 1]?.signature && (
                  <div className="relative group">
                    <Img
                      className="w-auto md:w-full h-48 md:h-40 rounded-xl border border-slate-200 shadow-sm object-cover cursor-pointer hover:shadow-md transition-shadow"
                      src={get_file_url(awbTrackingData.tracking_data[awbTrackingData.tracking_data.length - 1]?.signature)}
                    />
                    <span className="absolute bottom-2 left-2 bg-black/60 text-white text-[10px] px-2 py-0.5 rounded-lg font-medium">
                      Signature
                    </span>
                  </div>
                )}
                {awbTrackingData.tracking_data[awbTrackingData.tracking_data.length - 1]?.photo && (
                  <div className="relative group">
                    <Img
                      className="w-auto md:w-full h-48 md:h-40 rounded-xl border border-slate-200 shadow-sm object-cover cursor-pointer hover:shadow-md transition-shadow"
                      src={get_file_url(awbTrackingData.tracking_data[awbTrackingData.tracking_data.length - 1]?.photo)}
                    />
                    <span className="absolute bottom-2 left-2 bg-black/60 text-white text-[10px] px-2 py-0.5 rounded-lg font-medium">
                      Photo
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ═══════════ TRACKING TABLE ═══════════ */}
        {awbTrackingData.tracking_data.length ? (
          <div className="mx-auto mb-2">
            <div className="shadow-xl shadow-slate-200/50 bg-white/80 backdrop-blur-sm rounded-2xl overflow-hidden">
              <div className="flex items-center gap-2 px-4 py-2 border-b border-slate-100 bg-gradient-to-r from-indigo-50 to-white">
                <div className="w-1 h-4 bg-indigo-600 rounded-full"></div>
                <h2 className="text-xs font-bold text-slate-700 uppercase tracking-wider">Tracking Details</h2>
                <span className="ml-auto bg-indigo-100 text-indigo-700 text-[10px] font-bold px-2 py-0.5 rounded-full">
                  {awbTrackingData.tracking_data.length} events
                </span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[700px] text-sm">
                  <thead>
                    <tr className="bg-slate-50/80 text-left text-slate-500 text-[10px] uppercase tracking-wider">
                      <th className="px-4 py-2 font-semibold whitespace-nowrap">Date & Time</th>
                      <th className="px-4 py-2 font-semibold whitespace-nowrap">System DateTime</th>
                      <th className="px-4 py-2 font-semibold">Status</th>
                      <th className="px-4 py-2 font-semibold">Location</th>
                      <th className="px-4 py-2 font-semibold">Employee</th>
                      <th className="px-4 py-2 font-semibold">Device</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {[...awbTrackingData.tracking_data]
                      .sort((a, b) => a.id - b.id)
                      .map((d, i) => (
                        <tr key={`track-data-${i}`} className="hover:bg-blue-50/40 transition-colors">
                          <td className="px-4 py-1 text-slate-600 whitespace-nowrap">
                            {moment(d.updatedAt).format("DD-MM-YYYY") == "Invalid date"
                              ? d.updatedAt
                              : moment(d.updatedAt, "DD-MM-YYYY HH:mm").format("DD-MM-YYYY HH:mm")}
                          </td>
                          <td className="px-4 py-1 text-slate-950 whitespace-nowrap text-xs">
                            {moment(d.createdAt).format("DD-MM-YYYY") == "Invalid date"
                              ? d.createdAt
                              : moment(d.createdAt, "DD-MM-YYYY HH:mm").format("DD-MM-YYYY HH:mm")}
                          </td>
                          <td className="px-4 py-1">
                            <span className="inline-flex items-center rounded-full text-[14px] font-bold text-blue-800 whitespace-nowrap">
                              {d.message_code}
                            </span>
                          </td>
                          <td className="px-4 py-1 text-slate-700 text-xs">{d.subbranch?._pincode?._city?.city_name || "-"}</td>
                          <td className="px-4 py-1 text-slate-700 whitespace-nowrap text-xs">{d._addedBy ? d._addedBy.name : "-"}</td>
                          <td className="px-4 py-1">
                            <span
                              className={`text-[10px] font-semibold whitespace-nowrap ${d.isMobile == 1 ? "text-green-600" : "text-slate-400"}`}
                            >
                              {d.isMobile == 1 ? "📱 Mobile" : "💻 Web"}
                            </span>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        ) : null}

        {/* ═══════════ CHANGE LOG (Between Tracking and Comments) ═══════════ */}
        {userRole == roles.SUPER_ADMIN && awbTrackingData?.awbData?.awb_no ? (
          <div className="mx-auto mb-2">
            <div className="shadow-xl shadow-slate-200/50 bg-white/80 backdrop-blur-sm rounded-2xl overflow-hidden">
              <div className="flex items-center gap-2 px-4 py-2 border-b border-slate-100 bg-gradient-to-r from-slate-50 to-white">
                <div className="w-1 h-4 bg-slate-600 rounded-full"></div>
                <h2 className="text-xs font-bold text-slate-700 uppercase tracking-wider">Booking Change Log</h2>
                <div className="ml-auto flex items-center gap-2">
                  {showChangeLog && (
                    <span className="bg-slate-100 text-slate-700 text-[10px] font-bold px-2 py-0.5 rounded-full">
                      {changeLogEntries.length} entries
                    </span>
                  )}
                  <Button
                    size="sm"
                    className="bg-slate-50 text-slate-700 border border-slate-200 text-xs font-semibold hover:bg-slate-100 transition-colors"
                    onPress={loadChangeLog}
                    isDisabled={isChangeLogLoading || !awbTrackingData?.awbData?.id}
                  >
                    {isChangeLogLoading ? "Loading..." : showChangeLog ? "Reload Change Log" : "Load Change Log"}
                  </Button>
                </div>
              </div>

              {showChangeLog ? (
                changeLogEntries.length ? (
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[900px] text-sm">
                      <thead>
                        <tr className="bg-slate-50/80 text-left text-slate-500 text-[10px] uppercase tracking-wider">
                          <th className="px-4 py-2 font-semibold whitespace-nowrap">When</th>
                          <th className="px-4 py-2 font-semibold whitespace-nowrap">Source</th>
                          <th className="px-4 py-2 font-semibold whitespace-nowrap">User</th>
                          <th className="px-4 py-2 font-semibold whitespace-nowrap">Field</th>
                          <th className="px-4 py-2 font-semibold">From</th>
                          <th className="px-4 py-2 font-semibold">To</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {changeLogEntries.map((entry: any, idx: number) => {
                          const changes = getFormattedChanges(entry?.changeJson);
                          const when = moment(entry?.createdAt || entry?.updatedAt).isValid()
                            ? moment(entry?.createdAt || entry?.updatedAt).format("DD-MM-YYYY HH:mm:ss")
                            : "-";
                          const source = entry?._logSource || "BOOKING";
                          const userName = entry?._userId?.name || "-";

                          if (!changes.length) {
                            return (
                              <tr
                                key={`clog-empty-${idx}`}
                                className={`${
                                  source === "PICKUP"
                                    ? "bg-cyan-100/70 border-l-4 border-cyan-500"
                                    : "bg-indigo-100/70 border-l-4 border-indigo-500"
                                } hover:bg-slate-50/80`}
                              >
                                <td className="px-4 py-2 text-xs text-slate-600 whitespace-nowrap">{when}</td>
                                <td className="px-4 py-2 whitespace-nowrap">
                                  <span
                                    className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold ${
                                      source === "PICKUP" ? "bg-cyan-100 text-cyan-700" : "bg-indigo-100 text-indigo-700"
                                    }`}
                                  >
                                    {source}
                                  </span>
                                </td>
                                <td className="px-4 py-2 text-xs text-slate-700 font-semibold">{userName}</td>
                                <td className="px-4 py-2 text-xs text-slate-400" colSpan={3}>
                                  No field-level change found
                                </td>
                              </tr>
                            );
                          }

                          return changes.map((ch: any, changeIdx: number) => (
                            <tr
                              key={`clog-${idx}-${changeIdx}`}
                              className={`${
                                source === "PICKUP"
                                  ? "bg-cyan-100/70 border-l-4 border-cyan-500"
                                  : "bg-indigo-100/70 border-l-4 border-indigo-500"
                              } hover:bg-slate-50/80`}
                            >
                              <td className="px-4 py-2 text-xs text-slate-600 whitespace-nowrap">{changeIdx === 0 ? when : ""}</td>
                              <td className="px-4 py-2 whitespace-nowrap">
                                {changeIdx === 0 ? (
                                  <span
                                    className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold ${
                                      source === "PICKUP" ? "bg-cyan-100 text-cyan-700" : "bg-indigo-100 text-indigo-700"
                                    }`}
                                  >
                                    {source}
                                  </span>
                                ) : (
                                  ""
                                )}
                              </td>
                              <td className="px-4 py-2 text-xs text-slate-700 font-semibold">{changeIdx === 0 ? userName : ""}</td>
                              <td className="px-4 py-2 text-xs font-semibold text-slate-700 whitespace-nowrap">{ch.field}</td>
                              <td className="px-4 py-2 text-xs text-red-700 break-all">{ch.before}</td>
                              <td className="px-4 py-2 text-xs text-green-700 break-all">{ch.after}</td>
                            </tr>
                          ));
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="px-4 py-6 text-sm text-slate-500">No changelog found for this AWB.</div>
                )
              ) : (
                <div className="px-4 py-4 text-xs text-slate-500">Click "Load Change Log" to fetch entries.</div>
              )}
            </div>
          </div>
        ) : null}

        {/* ═══════════ COMMENTS TABLE ═══════════ */}
        {commentsList.length ? (
          <div className="mx-auto mb-2">
            <div className="shadow-xl shadow-slate-200/50 bg-white/80 backdrop-blur-sm rounded-2xl overflow-hidden">
              <div className="flex items-center gap-2 px-4 py-2 border-b border-slate-100 bg-gradient-to-r from-amber-50 to-white">
                <div className="w-1 h-4 bg-amber-500 rounded-full"></div>
                <h2 className="text-xs font-bold text-slate-700 uppercase tracking-wider">Comments</h2>
                <span className="ml-auto bg-amber-100 text-amber-700 text-[10px] font-bold px-2 py-0.5 rounded-full">
                  {commentsList.length}
                </span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[500px] text-sm">
                  <thead>
                    <tr className="bg-slate-50/80 text-left text-slate-500 text-[10px] uppercase tracking-wider">
                      <th className="px-4 py-2 font-semibold whitespace-nowrap">Date</th>
                      <th className="px-4 py-2 font-semibold">Time</th>
                      <th className="px-4 py-2 font-semibold">Comment</th>
                      <th className="px-4 py-2 font-semibold">Employee</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {commentsList.map((d, i) => (
                      <tr key={`comment-${d.id}`} className="hover:bg-amber-50/40 transition-colors">
                        <td className="px-4 py-2 text-slate-600 whitespace-nowrap text-xs">
                          {moment(d.createdAt, "DD-MM-YYYY HH:mm:ss").format("DD-MM-YYYY")}
                        </td>
                        <td className="px-4 py-2 text-slate-500 whitespace-nowrap text-xs">
                          {moment(d.createdAt, "DD-MM-YYYY HH:mm:ss").format("HH:mm")}
                        </td>
                        <td className="px-4 py-2 text-slate-700 text-xs">{d.comment}</td>
                        <td className="px-4 py-2 text-slate-600 whitespace-nowrap text-xs">{d._addedBy ? d._addedBy.name : "-"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </>
  );
};

export default TrackingAWB;
